import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Briefcase, Calendar, Clock, DollarSign, Pencil, Receipt, Trash2, User } from 'lucide-react'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { workersApi } from '@/services/workers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Empty } from '@/components/ui/empty'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import {
  getAssignmentStatusBadgeVariant,
  getAssignmentStatusLabel,
  getWorkdayEntryBlockedMessage,
  isTerminalAssignmentStatus,
  canDeleteAssignment,
  getAssignmentDeleteBlockedMessage,
  resolveAssignmentStatus,
} from '@/lib/assignment-status'
import { formatDateForLocale, formatDateTimeForLocale, useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getLocationReturnPath, resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { resolveEventFamilyAssignmentsBasePath } from '@/lib/event-family-assignments-route'
import { formatCurrency } from '@/lib/utils'
import { formatLoggedWork } from '@/lib/work-time-format'
import {
  formatAssignmentPayoutDisplay,
  getActualAssignmentPayoutDisplay,
  getEstimatedAssignmentPayoutDisplay,
  isCalculatedAssignmentPayType,
  normalizeAssignmentPayType,
  showsAssignmentPayTypeLabelOnly,
} from '@/lib/assignment-pay'
import { WorkDayEntryActionsMenu } from '@/components/workdays/work-day-entry-actions-menu'
import { AssignmentDeclineChainIndicator } from '@/components/assignments/assignment-decline-chain'
import { WorkflowStatusSelect } from '@/components/jobs/workflow-status-select'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { getWorkDayEntryStatusBadgeClassName, getWorkDayEntryStatusLabel } from '@/lib/workday-entry-status'
import { assignmentHasDeclineChain } from '@/lib/assignment-decline-chain'

const formatMoney = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return 'Not set'
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? formatCurrency(parsed) : String(value)
}

const formatAssignmentId = (value?: string | number | null) => `#${String(value ?? '').slice(0, 8)}`

const ASSIGNMENT_DELETE_CONFIRMATION_TEXT = 'delete assignment'

const toOptionalNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const formatEstimatedDuration = (
  value?: string | number | null,
  payType?: string | null,
) => {
  const parsed = toOptionalNumber(value)
  if (parsed == null) {
    return 'Not set'
  }

  switch (payType) {
    case 'per_day':
      return `${parsed} day${parsed === 1 ? '' : 's'}`
    case 'per_week':
      return `${parsed} week${parsed === 1 ? '' : 's'}`
    case 'per_month':
      return `${parsed} month${parsed === 1 ? '' : 's'}`
    case 'per_job':
      return `${parsed} job${parsed === 1 ? '' : 's'}`
    case 'per_hour':
    default:
      return `${parsed} hr${parsed === 1 ? '' : 's'}`
  }
}

const getStatusBadgeVariant = (status?: string | null) => getAssignmentStatusBadgeVariant(status)

const getStatusLabel = (status?: string | null) => getAssignmentStatusLabel(status)

const formatAuditActionLabel = (action?: string | null) => {
  if (!action) {
    return 'Assignment update'
  }

  return action
    .replace(/^assignment_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export default function AssignmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const currentReturnPath = getLocationReturnPath(location)
  const { locale: organizationDateLocale, timeZone: organizationTimeZone } =
    useOrganizationDateTimeFormat(serviceInstanceId)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')

  const searchParams = new URLSearchParams(location.search)
  const jobFilter = searchParams.get('job') || searchParams.get('job_id') || undefined
  const workerFilter = searchParams.get('worker') || searchParams.get('worker_id') || undefined

  const buildAssignmentsListPath = (fallbackJobId?: string | null, fallbackWorkerId?: string | null) => {
    const params = new URLSearchParams()
    const resolvedJobId = jobFilter || (fallbackJobId ?? undefined)
    const resolvedWorkerId = workerFilter || (fallbackWorkerId ?? undefined)

    if (resolvedJobId) {
      params.set('job_id', resolvedJobId)
    }

    if (resolvedWorkerId) {
      params.set('worker_id', resolvedWorkerId)
    }

    const defaultBasePath = location.pathname.startsWith('/events/')
      || location.pathname.startsWith('/event/')
      || location.pathname.startsWith('/committee/')
      || location.pathname.startsWith('/committees/')
      || location.pathname.startsWith('/task/')
      || location.pathname.startsWith('/tasks/')
      ? resolveEventFamilyAssignmentsBasePath(location.pathname)
      : '/assignments'

    return withServiceInstanceId(`${defaultBasePath}${params.toString() ? `?${params.toString()}` : ''}`, serviceInstanceId)
  }

  const buildWorkdaysPath = (assignment: { job_id: string; worker_id?: string | null }) => {
    const params = new URLSearchParams()
    params.set('job', assignment.job_id)
    if (assignment.worker_id) {
      params.set('worker', String(assignment.worker_id))
    }
    return withServiceInstanceId(`/workdays?${params.toString()}`, serviceInstanceId)
  }

  const assignmentQuery = useQuery({
    queryKey: ['assignment', id],
    queryFn: () => assignmentsApi.getAssignment(id as string),
    enabled: Boolean(id),
  })

  const configurationQuery = useQuery({
    queryKey: ['job-management-configuration', serviceInstanceId],
    queryFn: jobsApi.getManagementConfiguration,
  })

  const workHoursPerDay = configurationQuery.data?.work_hours_per_day ?? 8.5

  const workdayEntriesQuery = useQuery({
    queryKey: ['assignment-workday-entries', id],
    queryFn: () => assignmentsApi.getWorkDayEntries({
      job_id: assignmentQuery.data?.job_id,
      worker_id: assignmentQuery.data?.worker_id ?? undefined,
      page: 1,
      page_size: 500,
    }),
    enabled: Boolean(assignmentQuery.data?.job_id && assignmentQuery.data?.worker_id),
  })

  const assignmentAuditsQuery = useQuery({
    queryKey: ['assignment-audits', id],
    queryFn: () => assignmentsApi.getAssignmentAudits({ assignment: id, page: 1, page_size: 50 }),
    enabled: Boolean(id),
  })

  const workerSkillId = assignmentQuery.data?.worker_skill
  const workerSkillQuery = useQuery({
    queryKey: ['worker-skill', workerSkillId],
    queryFn: async () => {
      if (!workerSkillId) {
        return null
      }
      const allSkills = await workersApi.getWorkerSkills({})
      return allSkills.find((ws) => String(ws.id) === String(workerSkillId)) ?? null
    },
    enabled: Boolean(workerSkillId),
    staleTime: 60_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentsApi.deleteAssignment(assignmentId),
    onSuccess: () => {
      toast.success('Assignment deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
      if (assignmentQuery.data?.job_id) {
        queryClient.invalidateQueries({ queryKey: ['job-assignments', assignmentQuery.data.job_id] })
      }
      if (assignmentQuery.data?.worker_id) {
        queryClient.invalidateQueries({ queryKey: ['worker-assignments', assignmentQuery.data.worker_id] })
      }
      setDeleteConfirmationText('')
      navigate(resolveReturnToPath(location.search, buildAssignmentsListPath(assignmentQuery.data?.job_id, assignmentQuery.data?.worker_id)))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const generateInvoiceMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentsApi.generateAssignmentWorkerInvoice(assignmentId),
    onSuccess: (payload) => {
      toast.success(
        payload.worker_invoice_id
          ? `Worker invoice #${payload.worker_invoice_id} generated`
          : 'Worker invoice generated',
      )
      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
      queryClient.invalidateQueries({ queryKey: ['assignment-audits', id] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const assignmentData = assignmentQuery.data
  const workdayEntries = workdayEntriesQuery.data?.results ?? []
  const estimatedPayoutDisplay = assignmentData ? getEstimatedAssignmentPayoutDisplay(assignmentData) : { mode: 'dash' as const }
  const actualPayoutDisplay = assignmentData ? getActualAssignmentPayoutDisplay(assignmentData) : { mode: 'dash' as const }
  const assignmentPayType = assignmentData ? normalizeAssignmentPayType(assignmentData) : null
  const usesCalculatedPayout = assignmentPayType ? isCalculatedAssignmentPayType(assignmentPayType) : false
  const showsPayTypeLabelOnly = assignmentPayType ? showsAssignmentPayTypeLabelOnly(assignmentPayType) : false
  const assignmentTotalPay = toOptionalNumber(assignmentData?.total_pay)

  

  const workdayRollups = useMemo(() => {
    const totalEntries = workdayEntries.length
    const totalHours = workdayEntries.reduce((sum, entry) => sum + (toOptionalNumber(entry.hours) ?? 0), 0)
    const approvedPay = workdayEntries
      .filter((entry) => entry.status === 'accepted')
      .reduce((sum, entry) => sum + (toOptionalNumber(entry.total_pay) ?? 0), 0)
    const completedEntries = workdayEntries.filter((entry) => entry.status === 'completed' || entry.status === 'logged').length
    const latestWorkDate = [...workdayEntries]
      .map((entry) => entry.work_date)
      .filter(Boolean)
      .sort()
      .at(-1)

    return {
      totalEntries,
      totalHours,
      approvedPay,
      completedEntries,
      latestWorkDate,
    }
  }, [workdayEntries])

  const activityItems = useMemo(() => {
    if (!assignmentData) {
      return []
    }

    const auditItems = (assignmentAuditsQuery.data?.results ?? []).map((audit) => ({
      key: `audit-${audit.id}`,
      title: formatAuditActionLabel(audit.action),
      timestamp: audit.timestamp || audit.created_at || audit.updated_at,
      detail: [audit.value, audit.user_name ? `By ${audit.user_name}` : null].filter(Boolean).join(' • '),
    }))

    const baseItems = [
      {
        key: 'created',
        title: 'Assignment created',
        timestamp: assignmentData.created_at,
        detail: 'Initial worker allocation recorded.',
      },
      {
        key: 'updated',
        title: 'Assignment updated',
        timestamp: assignmentData.updated_at,
        detail: 'Latest assignment settings saved.',
      },
      ...workdayEntries.slice(0, 12).map((entry) => ({
        key: `workday-${entry.id}`,
        title: entry.status === 'completed' || entry.status === 'logged' ? 'Work logged' : 'Workday entry scheduled',
        timestamp: entry.updated_at || entry.created_at,
        detail: `${entry.work_date || 'Unknown date'}${entry.hours ? ` • ${entry.hours} hrs` : ''}${entry.total_pay ? ` • ${formatMoney(entry.total_pay)}` : ''}`,
      })),
      ...auditItems,
    ]

    return baseItems
      .filter((item) => item.timestamp)
      .sort((left, right) => new Date(right.timestamp as string).getTime() - new Date(left.timestamp as string).getTime())
      .slice(0, 8)
  }, [assignmentData, assignmentAuditsQuery.data?.results, workdayEntries])

  if (assignmentQuery.isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-52 rounded-xl" />
          <Skeleton className="h-52 rounded-xl" />
        </div>
      </div>
    )
  }

  if (assignmentQuery.error || !assignmentQuery.data) {
    return (
      <Card>
        <CardContent className="py-10">
          <Empty>
            <div className="text-center">
              <p className="text-lg font-medium">Assignment not found</p>
              <p className="text-sm text-muted-foreground">
                {assignmentQuery.error ? getErrorMessage(assignmentQuery.error) : 'The requested assignment could not be found.'}
              </p>
              <Button className="mt-4" variant="outline" asChild>
                <Link to={resolveReturnToPath(location.search, buildAssignmentsListPath())}>
                  Back to Assignments
                </Link>
              </Button>
            </div>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  const assignment = assignmentQuery.data
  const assignmentBlocksEdit = isTerminalAssignmentStatus(assignment?.status)
  const assignmentCompleted = resolveAssignmentStatus(assignment.status) === 'completed'
  const assignmentCanDelete = canDeleteAssignment(assignment)
  const editReturnPath = resolveReturnToPath(location.search, currentReturnPath)
  const assignmentEditBasePath = editReturnPath.includes('/events/')
    || editReturnPath.includes('/event/')
    || editReturnPath.includes('/committee/')
    || editReturnPath.includes('/committees/')
    || editReturnPath.includes('/task/')
    || editReturnPath.includes('/tasks/')
    ? resolveEventFamilyAssignmentsBasePath(editReturnPath)
    : '/assignments'
  const assignmentDetailsBasePath = assignmentEditBasePath
  const deleteConfirmationMatches = deleteConfirmationText === ASSIGNMENT_DELETE_CONFIRMATION_TEXT
  const workdayEntryBlockedMessage = getWorkdayEntryBlockedMessage(assignment?.status)
  const assignmentsReturnPath = resolveReturnToPath(location.search, buildAssignmentsListPath(assignment.job_id, assignment.worker_id))

  return (
    <div className="space-y-6">
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open)
        if (!open && !deleteMutation.isPending) {
          setDeleteConfirmationText('')
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {assignment.worker_name || 'the worker'} from {formatJobTitleWithId({
                jobId: assignment.job_id,
                title: assignment.job_title,
                jobRef: assignment.job_ref,
              })}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Input
              value={deleteConfirmationText}
              onChange={(event) => setDeleteConfirmationText(event.target.value)}
              placeholder="Type delete assignment"
              autoComplete="off"
              disabled={deleteMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">Confirmation is case-sensitive and must exactly match: {ASSIGNMENT_DELETE_CONFIRMATION_TEXT}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending || !assignmentCanDelete || !deleteConfirmationMatches}
              onClick={(event) => {
                event.preventDefault()
                if (assignmentCanDelete && deleteConfirmationMatches) {
                  deleteMutation.mutate(assignment.id)
                }
              }}
            >
              Delete Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex gap-2 pt-1">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(assignmentsReturnPath)}
                title="Back to assignments"
                className="h-10 w-10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                asChild
                title="Back to job details"
                className="h-10 w-10"
              >
                <Link to={withServiceInstanceId(`/job/${assignment.job_id}`, serviceInstanceId)}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-foreground">Assignment {formatAssignmentId(assignment.id)}</h1>
                <Badge variant={getStatusBadgeVariant(assignment.status)}>
                  {getStatusLabel(assignment.status)}
                </Badge>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                <JobTitleLink
                  jobId={assignment.job_id}
                  title={assignment.job_title}
                  jobRef={assignment.job_ref}
                  serviceInstanceId={serviceInstanceId}
                  returnPath={currentReturnPath}
                  className="font-medium"
                />
                {' · '}
                {assignment.role_label || assignment.planner_resource_label || 'Role slot'}
                {' · '}
                {assignment.worker_id
                  ? (assignment.worker_name || `Worker #${assignment.worker_id}`)
                  : 'Unassigned'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="default" asChild>
              <Link to={withServiceInstanceId(`/job/${assignment.job_id}`, serviceInstanceId)} title="View job details">
                <Briefcase className="mr-2 h-4 w-4" />
                View Job
              </Link>
            </Button>
            <WorkflowStatusSelect
              entityType="assignment"
              entityId={assignment.id}
              currentValue={getStatusLabel(assignment.status)}
              className="w-44"
              onApplied={() => {
                queryClient.invalidateQueries({ queryKey: ['assignment', id] })
                queryClient.invalidateQueries({ queryKey: ['assignments'] })
                queryClient.invalidateQueries({ queryKey: ['job-assignments', assignment.job_id] })
                queryClient.invalidateQueries({ queryKey: ['assignment-audits', id] })
              }}
            />
            <Button
              variant="outline"
              disabled={!assignmentCompleted || generateInvoiceMutation.isPending}
              title={
                assignmentCompleted
                  ? 'Generate worker invoice for this completed assignment'
                  : 'Complete this assignment before generating the worker invoice'
              }
              onClick={() => generateInvoiceMutation.mutate(assignment.id)}
            >
              {generateInvoiceMutation.isPending ? (
                <Spinner className="mr-2" />
              ) : (
                <Receipt className="mr-2 h-4 w-4" />
              )}
              {generateInvoiceMutation.isPending ? 'Generating...' : 'Generate invoice'}
            </Button>
            <Button variant="outline" asChild={!assignmentBlocksEdit} disabled={assignmentBlocksEdit}>
              {assignmentBlocksEdit ? (
                <>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </>
              ) : (
                <Link to={withReturnTo(withServiceInstanceId(`${assignmentEditBasePath}/${assignment.id}/edit${location.search || ''}`, serviceInstanceId), editReturnPath)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              )}
            </Button>
            <Button
              variant="destructive"
              disabled={!assignmentCanDelete}
              title={assignmentCanDelete ? undefined : getAssignmentDeleteBlockedMessage()}
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {assignmentHasDeclineChain(assignment) ? (
        <AssignmentDeclineChainIndicator
          assignment={assignment}
          serviceInstanceId={serviceInstanceId}
          returnPath={currentReturnPath}
          detailBasePath={assignmentDetailsBasePath}
          variant="detail"
          locale={organizationDateLocale}
          timeZone={organizationTimeZone}
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assignment Overview</CardTitle>
            <CardDescription>Core links and assignment status.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Job</p>
                <JobTitleLink
                  jobId={assignment.job_id}
                  title={assignment.job_title}
                  jobRef={assignment.job_ref}
                  serviceInstanceId={serviceInstanceId}
                  returnPath={currentReturnPath}
                  className="font-medium"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Worker</p>
                {assignment.worker_id ? (
                  <Link to={withServiceInstanceId(`/workers/${assignment.worker_id}`, serviceInstanceId)} className="font-medium text-primary hover:underline">
                    {assignment.worker_name || `Worker #${assignment.worker_id}`}
                  </Link>
                ) : (
                  <span className="font-medium text-amber-700">Unassigned</span>
                )}
              </div>
            </div>
            {assignment.status ? (
              <div className="flex items-center gap-3 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Workflow Status</p>
                  <p className="font-medium">{getStatusLabel(assignment.status)}</p>
                </div>
              </div>
            ) : null}
            {assignment.role ? (
              <div className="flex items-center gap-3 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Role</p>
                  <p className="font-medium">{assignment.role}</p>
                </div>
              </div>
            ) : null}
            {assignment.worker_skill ? (
              <div className="flex items-center gap-3 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Worker Skill</p>
                  <p className="font-medium">
                    {workerSkillQuery.isLoading
                      ? 'Loading...'
                      : workerSkillQuery.error
                        ? 'Error loading skill'
                        : workerSkillQuery.data
                          ? [
                              workerSkillQuery.data.skill_role_title
                                || workerSkillQuery.data.skill_role_name
                                || workerSkillQuery.data.skill_role,
                              workerSkillQuery.data.job_category_title
                                || workerSkillQuery.data.job_category_name
                                || workerSkillQuery.data.job_category,
                            ].filter(Boolean).join(' · ')
                          : String(assignment.worker_skill)}
                  </p>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Compensation</CardTitle>
            <CardDescription>Base pay plus any additional pay amounts configured for this assignment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Pay Type</p>
                <p className="font-medium">{assignment.pay_type_label || assignment.pay_type || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Estimated Duration</p>
                <p className="font-medium">{formatEstimatedDuration(assignment.estimated_duration, assignment.pay_type)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Base Pay</p>
                <p className="font-medium">{formatMoney(assignment.pay)}</p>
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-border/60 p-4">
              <div>
                <p className="text-sm font-medium text-foreground">Additional pay</p>
                <p className="text-xs text-muted-foreground">Added on top of base pay.</p>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Night Pay</p>
                  <p className="font-medium">{formatMoney(assignment.night_pay)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Holiday Pay</p>
                  <p className="font-medium">{formatMoney(assignment.holiday_pay)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Peak Pay</p>
                  <p className="font-medium">{formatMoney(assignment.peak_pay)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Flat Pay</p>
                  <p className="font-medium">{formatMoney(assignment.pay_flat)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Special Pay</p>
                  <p className="font-medium">{formatMoney(assignment.special_pay)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Estimated Worker Payout</p>
                <p className="font-medium">{formatAssignmentPayoutDisplay(estimatedPayoutDisplay, formatMoney)}</p>
                <p className="text-xs text-muted-foreground">
                  {usesCalculatedPayout
                    ? 'Based on assignment rate and estimated duration.'
                    : assignmentPayType === 'per_day'
                      ? 'Day rate shown for reference only.'
                      : showsPayTypeLabelOnly
                        ? 'Pay scale type shown for reference only.'
                        : 'Not applicable for this pay scale.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Receipt className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Actual Payout</p>
                <p className="font-medium">
                  {usesCalculatedPayout && assignmentTotalPay === null && workdayEntriesQuery.isLoading
                    ? 'Calculating...'
                    : formatAssignmentPayoutDisplay(actualPayoutDisplay, formatMoney)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Payment to company</p>
                <p className="text-xs text-muted-foreground">Cash collected on the job that the worker must return to the company.</p>
                <p className="font-medium">{formatMoney(assignment.payment_due_to_company)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Workday Rollup</CardTitle>
            <CardDescription>Summary of work logged against this assignment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Workday Entries</p>
                <p className="font-medium">{workdayEntriesQuery.isLoading ? 'Loading...' : workdayRollups.totalEntries}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Logged Work</p>
                <p className="font-medium">{workdayEntriesQuery.isLoading ? 'Loading...' : formatLoggedWork(workdayRollups.totalHours, assignmentData?.pay_type, workHoursPerDay)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Completed or Logged Entries</p>
                <p className="font-medium">{workdayEntriesQuery.isLoading ? 'Loading...' : workdayRollups.completedEntries}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Latest Work Date</p>
                <p className="font-medium">
                  {workdayEntriesQuery.isLoading
                    ? 'Loading...'
                    : workdayRollups.latestWorkDate
                      ? formatDateForLocale(workdayRollups.latestWorkDate, organizationDateLocale)
                      : 'No work logged'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Activity Timeline</CardTitle>
            <CardDescription>Recent assignment status changes, updates, and workday events.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {activityItems.length > 0 ? (
              activityItems.map((item) => (
                <div key={item.key} className="rounded-xl border border-border/60 p-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-foreground">{item.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                    </div>
                    <p className="shrink-0 text-xs text-muted-foreground">{formatDateTimeForLocale(item.timestamp, organizationDateLocale, 'Not recorded', organizationTimeZone)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No assignment activity is available yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base">Work Log Entries</CardTitle>
                <CardDescription>Recent workday entries for this worker on this job.</CardDescription>
              </div>
              <Button variant="outline" asChild={!assignmentBlocksEdit} disabled={assignmentBlocksEdit}>
                {assignmentBlocksEdit ? (
                  <>
                    <Calendar className="mr-2 h-4 w-4" />
                    Work Days
                  </>
                ) : (
                  <Link to={buildWorkdaysPath(assignment)}>
                    <Calendar className="mr-2 h-4 w-4" />
                    Work Days
                  </Link>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {assignmentBlocksEdit ? (
              <p className="mb-4 text-sm text-muted-foreground">{workdayEntryBlockedMessage}</p>
            ) : null}
            {workdayEntriesQuery.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-14 w-full rounded-xl" />
                ))}
              </div>
            ) : workdayEntries.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-border/70">
                <table className="w-full text-sm">
                  <thead className="bg-muted/35 text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Hours</th>
                      <th className="px-4 py-3">Pay</th>
                      <th className="px-4 py-3">Check In / Out</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workdayEntries
                      .slice()
                      .sort((left, right) => String(right.work_date || '').localeCompare(String(left.work_date || '')))
                      .slice(0, 10)
                      .map((entry) => (
                        <tr key={String(entry.id)} className="border-t border-border/60">
                          <td className="px-4 py-3">{entry.work_date ? formatDateForLocale(entry.work_date, organizationDateLocale, 'Unknown', organizationTimeZone) : 'Unknown'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={getWorkDayEntryStatusBadgeClassName(entry.status)}>
                              {entry.status_label || getWorkDayEntryStatusLabel(entry.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">{entry.hours ? `${entry.hours} hrs` : '-'}</td>
                          <td className="px-4 py-3">{entry.total_pay ? formatMoney(entry.total_pay) : '-'}</td>
                          <td className="px-4 py-3">
                            <span className="text-muted-foreground">
                              {entry.check_in || entry.check_out
                                ? `${formatDateTimeForLocale(entry.check_in, organizationDateLocale, '-', organizationTimeZone) } / ${formatDateTimeForLocale(entry.check_out, organizationDateLocale, '-', organizationTimeZone)}`
                                : '-'}
                            </span>
                          </td>
                          <td className="relative px-4 py-3">
                            <WorkDayEntryActionsMenu
                              entry={entry}
                              serviceInstanceId={serviceInstanceId}
                              assignmentId={assignment.id}
                              assignmentBlocked={assignmentBlocksEdit}
                              assignmentStatus={assignment?.status}
                              alwaysShowTrigger
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No workday entries have been logged for this assignment yet.</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Notes</CardTitle>
            <CardDescription>Additional assignment metadata when available.</CardDescription>
          </CardHeader>
          <CardContent>
            {assignment.notes ? (
              <p className="text-sm leading-6 text-foreground">{assignment.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No notes recorded for this assignment.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}